import * as THREE from 'three'
import type { CompressedSplatData } from '@/plugins/gsplat/core/CompressedSplatData'
import type { GSplatSogsData } from '@/plugins/gsplat/core/GSplatSogsData'
import {
  GSPLAT_INSTANCE_SIZE,
  setBatchedSplatQuadGeometry,
} from '@/plugins/gsplat/core/gsplatBatching'
import type { SogsData } from '@/plugins/gsplat/core/SogsData'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import type { IGSplatMaterial } from '@/plugins/gsplat/types/ResourceTypes'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:lod')
const EMPTY_CENTERS = new Float32Array(0)

// 不从 GSplatWorkBuffer 导入，因为那里的 WorkBufferRenderInfo 设计不同
// 我们在这里定义自己的 WorkBufferRenderInfo

/**
 * WorkBufferRenderInfo - 用于渲染到工作缓冲区的渲染信息
 * 对应 PlayCanvas 的 WorkBufferRenderInfo
 *
 * @remarks
 * 缓存特定配置的材质和渲染器，避免重复创建
 */
export class WorkBufferRenderInfo {
  /** 配置键（用于缓存） */
  readonly key: string

  /** Shader 材质 */
  readonly material: GaussianSplatMaterial

  /** 渲染器（用于绘制） */
  renderer: THREE.WebGLRenderer | null = null

  /** 是否仅渲染颜色 */
  readonly colorOnly: boolean

  constructor(key: string, material: GaussianSplatMaterial, colorOnly: boolean = false) {
    this.key = key
    this.material = material
    this.colorOnly = colorOnly
  }

  /**
   * 销毁资源
   */
  destroy(): void {
    this.material.dispose()
    this.renderer = null
  }
}

/**
 * 纹理尺寸计算结果
 */
export interface TextureSize {
  /** 纹理宽度，像素 */
  x: number
  /** 纹理高度，像素 */
  y: number
}

/**
 * GSplat 资源基类
 * 对应 PlayCanvas 的 GSplatResourceBase
 *
 * 职责：
 * 1. 管理 Splat 数据（SplatData 或 SogsData）
 * 2. 提供实例化渲染的基础网格
 * 3. 创建和管理 GPU 纹理
 * 4. 维护引用计数
 * 5. 缓存 WorkBufferRenderInfo
 *
 * @remarks
 * 子类需要实现：
 * - configureMaterial(): 配置材质参数
 * - configureMaterialDefines(): 配置材质宏定义
 * - evalTextureSize(): 计算纹理尺寸
 *
 * @example
 * ```typescript
 * class MySplatResource extends GSplatResourceBase {
 *   configureMaterial(material: GaussianSplatMaterial) {
 *     material.uniforms.uTexture.value = this.texture;
 *   }
 *
 *   configureMaterialDefines(defines: Map<string, string>) {
 *     defines.set('USE_SH', '1');
 *   }
 *
 *   evalTextureSize(count: number): TextureSize {
 *     const width = Math.ceil(Math.sqrt(count));
 *     return { x: width, y: width };
 *   }
 * }
 * ```
 */
export abstract class GSplatResourceBase {
  /** 资源 ID */
  readonly id: number

  /** Three.js 渲染器（用于纹理创建等） */
  readonly renderer: THREE.WebGLRenderer

  /** Splat 数据（PLY 格式或 SOGS 压缩格式或压缩格式） */
  readonly gsplatData: SplatData | SogsData | GSplatSogsData | CompressedSplatData

  /** 中心点数组（用于排序和剔除）；销毁后替换为空数组以主动断开大内存。 */
  private _centers: Float32Array

  /** 防止重复释放基础 GPU 资源。 */
  private _destroyed = false

  /** 包围盒 */
  readonly aabb: THREE.Box3

  /** 基础网格（用于实例化渲染） */
  mesh: THREE.Mesh | null = null

  /** 实例索引缓冲区（用于 instanced rendering） */
  instanceIndices: THREE.InstancedBufferAttribute | null = null

  /** WorkBufferRenderInfo 缓存 */
  private workBufferRenderInfos: Map<string, WorkBufferRenderInfo> = new Map()

  /** 引用计数 */
  private _refCount: number = 0

  /** 全局 ID 计数器 */
  private static nextId: number = 0

  /** 上下文恢复事件处理函数 */
  protected _onContextRestored: (() => void) | null = null

  /**
   * 实例大小（每个实例包含多少个 splat）
   * 对应 PlayCanvas 的 instanceSize
   */
  static readonly INSTANCE_SIZE = GSPLAT_INSTANCE_SIZE

  constructor(
    renderer: THREE.WebGLRenderer,
    gsplatData: SplatData | SogsData | GSplatSogsData | CompressedSplatData
  ) {
    this.id = GSplatResourceBase.nextId++
    this.renderer = renderer
    this.gsplatData = gsplatData

    // 提取中心点
    const centers = gsplatData.getCenters()
    if (!centers) {
      throw new Error('[GSplatResourceBase] Failed to get centers from gsplatData')
    }
    this._centers = centers

    // 计算包围盒
    this.aabb = new THREE.Box3()
    gsplatData.calcAabb(this.aabb)

    // 构造函数抛错时 subclass/finalizer 无法取得 this，因此先用局部 mesh 完成后续步骤；
    // 任一步失败都在这里回收已创建的 geometry/material。
    const mesh = GSplatResourceBase.createMesh()
    try {
      const instanceIndices = GSplatResourceBase.createInstanceIndices(this.numSplats)
      mesh.geometry.boundingBox = this.aabb.clone()
      this.mesh = mesh
      this.instanceIndices = instanceIndices
    } catch (error) {
      GSplatResourceBase.disposeMesh(mesh)
      throw error
    }
  }

  /**
   * 销毁资源
   */
  destroy(): void {
    if (this._destroyed) {
      return
    }
    this._destroyed = true
    this._centers = EMPTY_CENTERS

    // 移除上下文恢复事件监听
    if (this._onContextRestored) {
      this.renderer.domElement.removeEventListener('webglcontextrestored', this._onContextRestored)
      this._onContextRestored = null
    }

    // 销毁网格
    if (this.mesh) {
      GSplatResourceBase.disposeMesh(this.mesh)
      this.mesh = null
    }

    // 销毁实例索引
    this.instanceIndices = null

    // 销毁 WorkBufferRenderInfo 缓存
    this.workBufferRenderInfos.forEach((info) => info.destroy())
    this.workBufferRenderInfos.clear()
  }

  /** 获取中心点数组；资源销毁后返回共享空数组。 */
  get centers(): Float32Array {
    return this._centers
  }

  /**
   * 增加引用计数
   */
  incRefCount(): void {
    this._refCount++
  }

  /**
   * 减少引用计数
   */
  decRefCount(): void {
    if (this._refCount <= 0) {
      this._refCount = 0
      log.warn(`GSplatResourceBase[${this.id}]: refCount underflow prevented`)
      return
    }
    this._refCount--
  }

  /**
   * 获取引用计数
   */
  get refCount(): number {
    return this._refCount
  }

  /**
   * 获取 Splat 数量
   */
  get numSplats(): number {
    return this.gsplatData.numSplats
  }

  /**
   * 获取或创建 WorkBufferRenderInfo
   *
   * @param useIntervals - 是否使用 LOD 间隔
   * @param colorTextureFormat - 颜色纹理格式（未使用，保留以兼容 PlayCanvas API）
   * @param colorOnly - 是否仅渲染颜色
   * @returns WorkBufferRenderInfo 实例
   *
   * @remarks
   * 注意：此方法不应该被调用！
   * PlayCanvas 中每个 resource 都有自己的 getWorkBufferRenderInfo 实现
   * 对于 Octree LOD，应该使用 GSplatSogsResource.getWorkBufferRenderInfo
   * 对于单文件，应该使用 geometry 直接绑定纹理
   */
  getWorkBufferRenderInfo(
    useIntervals: boolean,
    // NOTE: 保留 any 类型以兼容 PlayCanvas API，实际未使用此参数
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _colorTextureFormat: any,
    colorOnly: boolean = false
  ): WorkBufferRenderInfo {
    log.warn(
      '[GSplatResourceBase] getWorkBufferRenderInfo called on base class! This should be overridden by subclass.'
    )

    // 构建缓存键
    const defines = new Map<string, string>()
    this.configureMaterialDefines(defines)

    if (useIntervals) {
      defines.set('GSPLAT_LOD', '')
    }

    if (colorOnly) {
      defines.set('GSPLAT_COLOR_ONLY', '')
    }

    const key = Array.from(defines.entries())
      .map(([k, v]) => `${k}=${v}`)
      .join(';')

    // 获取或创建缓存
    let info = this.workBufferRenderInfos.get(key)
    if (!info) {
      // 创建一个基础材质（子类应该override这个方法）
      const material = new GaussianSplatMaterial({
        depthWrite: false,
        depthTest: true,
        transparent: true,
      })

      // 配置材质
      this.configureMaterial(material)

      // 应用 defines
      defines.forEach((value, key) => {
        material.defines = material.defines || {}
        material.defines[key] = value
      })

      material.needsUpdate = true

      // 创建新的 RenderInfo
      info = new WorkBufferRenderInfo(key, material, colorOnly)
      this.workBufferRenderInfos.set(key, info)
    }

    return info
  }

  /**
   * 创建基础网格（用于实例化渲染）
   * 对应 PlayCanvas 的 createMesh
   *
   * @returns 基础网格
   */
  static createMesh(): THREE.Mesh {
    const geometry = new THREE.BufferGeometry()
    let material: THREE.MeshBasicMaterial | null = null
    try {
      setBatchedSplatQuadGeometry(geometry)
      material = new THREE.MeshBasicMaterial()
      return new THREE.Mesh(geometry, material)
    } catch (error) {
      geometry.dispose()
      material?.dispose()
      throw error
    }
  }

  private static disposeMesh(mesh: THREE.Mesh): void {
    mesh.geometry.dispose()
    if (Array.isArray(mesh.material)) {
      for (const material of mesh.material) {
        material.dispose()
      }
    } else {
      mesh.material.dispose()
    }
  }

  /**
   * 创建实例索引缓冲区
   * 对应 PlayCanvas 的 createInstanceIndices
   *
   * @param splatCount - Splat 数量
   * @returns 实例索引属性
   */
  static createInstanceIndices(splatCount: number): THREE.InstancedBufferAttribute {
    const instanceSize = GSplatResourceBase.INSTANCE_SIZE
    const numSplats = Math.ceil(splatCount / instanceSize) * instanceSize
    const numInstances = numSplats / instanceSize

    const indexData = new Uint32Array(numInstances)
    for (let i = 0; i < numInstances; i++) {
      indexData[i] = i * instanceSize
    }

    return new THREE.InstancedBufferAttribute(indexData, 1)
  }

  /**
   * 配置材质参数
   * 子类必须实现，用于设置 uniforms 等
   *
   * @param material - 要配置的材质
   */
  abstract configureMaterial(material: IGSplatMaterial): void

  /**
   * 配置材质宏定义
   * 子类必须实现，用于设置 shader defines
   *
   * @param defines - 宏定义 Map
   */
  abstract configureMaterialDefines(defines: Map<string, string>): void

  /**
   * 计算纹理尺寸
   * 子类必须实现，根据 splat 数量计算纹理宽高
   *
   * @param count - Splat 数量
   * @returns 纹理尺寸
   */
  abstract evalTextureSize(count: number): TextureSize

  /**
   * 创建纹理的辅助方法
   *
   * @param name - 纹理名称
   * @param format - 纹理格式
   * @param type - 数据类型
   * @param size - 纹理尺寸
   * @param data - 初始数据（可选）
   * @returns 创建的纹理
   */
  protected createTexture(
    name: string,
    format: THREE.PixelFormat,
    type: THREE.TextureDataType,
    size: TextureSize,
    data?: ArrayBufferView
  ): THREE.DataTexture {
    // NOTE: Three.js DataTexture 构造函数接受 undefined 作为数据参数
    const texture = new THREE.DataTexture(data as any, size.x, size.y, format, type)

    texture.name = name
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false

    // 为整数纹理自动设置 internalFormat
    // 关键修复：Three.js 需要明确设置 internalFormat，否则会导致格式不匹配错误
    if (type === THREE.UnsignedIntType) {
      if (format === THREE.RGBAIntegerFormat) {
        texture.internalFormat = 'RGBA32UI'
      } else if (format === THREE.RGIntegerFormat) {
        texture.internalFormat = 'RG32UI'
      } else if (format === THREE.RedIntegerFormat) {
        texture.internalFormat = 'R32UI'
      }
    } else if (type === THREE.IntType) {
      if (format === THREE.RGBAIntegerFormat) {
        texture.internalFormat = 'RGBA32I'
      } else if (format === THREE.RGIntegerFormat) {
        texture.internalFormat = 'RG32I'
      } else if (format === THREE.RedIntegerFormat) {
        texture.internalFormat = 'R32I'
      }
    } else if (type === THREE.HalfFloatType) {
      if (format === THREE.RGBAFormat) {
        texture.internalFormat = 'RGBA16F'
      } else if (format === THREE.RGFormat) {
        texture.internalFormat = 'RG16F'
      } else if (format === THREE.RedFormat) {
        texture.internalFormat = 'R16F'
      }
    }

    texture.needsUpdate = true

    return texture
  }
}
